import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import type { DeclaredSource, FallbackKind } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Panel } from "@/components/ui/Panel";

/** The three kinds, in the words the attorney reads them by. */
export const KIND_LABEL: Record<FallbackKind, string> = {
  class: "Class",
  member: "Member",
  value: "Value",
};

/** The answers to "Where is it from?", as the add-element form offers them. */
const SOURCES: readonly { value: DeclaredSource; label: string }[] = [
  { value: "disclosure", label: "The invention disclosure" },
  { value: "supporting_notes", label: "Supporting notes" },
  { value: "none", label: "Not in any document, I'm adding it" },
];

/** What a new rung carries, as the card hands it up. */
export interface AddedRung {
  kind: FallbackKind;
  text: string;
  parentId: string | null;
  declaredSource: DeclaredSource;
}

/** The rung's kind, from what its place allows: under a class, never another class. */
function KindField({
  scope,
  kinds,
  value,
  onChange,
}: {
  scope: string;
  kinds: readonly FallbackKind[];
  value: FallbackKind;
  onChange: (kind: FallbackKind) => void;
}) {
  return (
    <div>
      <MicroLabel
        as="label"
        size="sm"
        htmlFor={`rung-kind-${scope}`}
        className="block"
      >
        Kind
      </MicroLabel>
      <Select
        size="dense"
        id={`rung-kind-${scope}`}
        value={value}
        onChange={(event) => {
          onChange(event.target.value as FallbackKind);
        }}
        className="mt-1"
      >
        {kinds.map((option) => (
          <option key={option} value={option}>
            {KIND_LABEL[option]}
          </option>
        ))}
      </Select>
    </div>
  );
}

/** "Where is it from?": chosen, never defaulted, as the element's own form has it. */
function RungSourceField({
  scope,
  value,
  onChange,
}: {
  scope: string;
  value: DeclaredSource | "";
  onChange: (value: DeclaredSource | "") => void;
}) {
  return (
    <div>
      <MicroLabel
        as="label"
        size="sm"
        htmlFor={`rung-source-${scope}`}
        className="block"
      >
        Where is it from?
      </MicroLabel>
      <Select
        size="dense"
        id={`rung-source-${scope}`}
        value={value}
        onChange={(event) => {
          onChange(event.target.value as DeclaredSource | "");
        }}
        className="mt-1"
      >
        <option value="" disabled>
          Choose where it comes from
        </option>
        {SOURCES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </div>
  );
}

/** The rung's words, which every rung must carry. */
function RungTextField({
  scope,
  fieldRef,
  value,
  onChange,
}: {
  scope: string;
  fieldRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <>
      <MicroLabel
        as="label"
        size="sm"
        htmlFor={`rung-text-${scope}`}
        className="mt-2 block"
      >
        Fallback text
      </MicroLabel>
      <Textarea
        size="dense"
        id={`rung-text-${scope}`}
        ref={fieldRef}
        rows={1}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        placeholder="e.g. a narrower term for the element"
        className="mt-1 text-[12px]"
      />
    </>
  );
}

/**
 * The form that adds a rung, under the element or under one of its classes.
 *
 * Under a class only a member or a value may go, since a class holds no class;
 * the kind control offers what the place allows and nothing else.
 */
export function AddRungForm({
  parentId,
  busy,
  onAdd,
  onClose,
}: {
  parentId: string | null;
  busy: boolean;
  /** Resolves when the rung is stored. A rejection leaves the form standing. */
  onAdd: (rung: AddedRung) => Promise<unknown>;
  onClose: () => void;
}) {
  const kinds: readonly FallbackKind[] =
    parentId === null ? ["class", "member", "value"] : ["member", "value"];
  const [kind, setKind] = useState<FallbackKind>(kinds[0]);
  const [text, setText] = useState("");
  const [source, setSource] = useState<DeclaredSource | "">("");
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    fieldRef.current?.focus();
  }, []);
  const scope = parentId ?? "element";
  return (
    <Panel
      as="form"
      pad="snug"
      data-testid={`add-rung-${scope}`}
      className="mt-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        const wording = text.trim();
        if (wording === "" || source === "") return;
        // Closed only once the rung is stored: the form holds the only copy.
        void onAdd({
          kind,
          text: wording,
          parentId,
          declaredSource: source,
        }).then(onClose, () => undefined);
      }}
    >
      <div className="flex flex-wrap gap-2">
        <KindField
          scope={scope}
          kinds={kinds}
          value={kind}
          onChange={setKind}
        />
        <RungSourceField scope={scope} value={source} onChange={setSource} />
      </div>
      <RungTextField
        scope={scope}
        fieldRef={fieldRef}
        value={text}
        onChange={setText}
      />
      <div className="mt-2 flex gap-2">
        <Button
          type="submit"
          variant="secondary"
          size="dense"
          disabled={busy || text.trim() === "" || source === ""}
        >
          Add fallback
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="dense"
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </Panel>
  );
}
